"use client";

import React, { useRef, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "@/components/ui/dialog";
import FeedbackIcon from "@/assets/images/FEEDBACK-ICON.svg";
import Image from "next/image";
import CustomInput from "@/components/ui/customInput";
import Logo from "@/assets/images/logo.png";
import { useToast } from "@/components/ui/use-toast";
import { Controller, useForm } from "react-hook-form";
import Feedback from "@/assets/images/feedback.png";
import { Textarea } from "@/components/ui/textarea";
import { useContactUsMutation } from "@/redux/apiSlices/formApi";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Options
const feedbackOptions = ["Website Feedback", "Store Feedback", "Associate Feedback"];
const ratingOptions = ["Terrible", "Poor", "Average", "Good", "Excellent"];

const FeedbackStep0 = ({ onContinue, selectedOption, setSelectedOption }) => (
  <div>
    <h4 className="text-[14px] leading-[20px] mb-[22px]">
      We appreciate your feedback. Your opinions will help us provide you the best shopping experience - in store and online.
    </h4>
    <h4 className="text-[14px] leading-[20px] mb-[17px] font-semibold">
      What type of feedback would you like to provide?
    </h4>
    <div className="mb-[46px] flex gap-[4px] flex-wrap gap-y-2">
      {feedbackOptions.map((option) => (
        <Button
          key={option}
          variant={selectedOption === option ? "default" : "outline"}
          className={`text-[12px] leading-[18px] px-4 py-[9px] rounded-[4px] border ${
            selectedOption === option ? "bg-[#0B223C] text-white border-[#0B223C]" : "text-[#424242] border-[#DBDBDB]"
          }`}
          onClick={() => setSelectedOption(option)}
        >
          {option}
        </Button>
      ))}
    </div>
    <Button
      onClick={onContinue}
      disabled={!selectedOption}
      className="w-[126px] rounded-[4px] h-[46px] bg-[#0B223C] disabled:bg-gray-400 disabled:cursor-not-allowed"
    >
      Continue
    </Button>
  </div>
);

const FeedbackStep1 = ({
  formRef,
  onSubmit,
  selectedOption,
  handleSubmit,
  control,
  onBack,
  selectedRating,
  setSelectedRating,
  errors,
}) => {
  const feedbackLabels = {
    "Store Feedback": "the store",
    "Website Feedback": "the website",
    "Associate Feedback": "the associate",
  };

  return (
    <div>
      <h4 className="text-[13px] leading-[19px] text-[#252525] mb-[22px]">
        We appreciate your feedback. Your opinions will help us provide you the best shopping experience — in store and online.
      </h4>

      {/* Rating Section */}
      <div className="mb-[26px]">
        <h4 className="text-[13px] leading-[19px] text-[#252525] mb-[12px] font-semibold">
          Rate your overall experience with {feedbackLabels[selectedOption] || "us"}:
        </h4>
        <div className="flex gap-[12px] flex-wrap gap-y-2">
          {ratingOptions.map((rating) => (
            <Button
              key={rating}
              variant={selectedRating === rating ? "default" : "outline"}
              className={`text-[12px] leading-[18px] px-4 py-[9px] rounded-[4px] border h-[36px] ${
                selectedRating === rating
                  ? "bg-[#0B223C] text-white border-[#0B223C]"
                  : "text-[#424242] border-[#DBDBDB]"
              }`}
              onClick={() => setSelectedRating(rating)}
            >
              {rating}
            </Button>
          ))}
        </div>
      </div>

      {/* Feedback Form */}
      <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>

        {/* Conditional Dropdowns */}
        {selectedOption === "Associate Feedback" && (
          <>
            <FeedbackDropdown
              label="I am providing feedback for a:"
              name="associateType"
              control={control}
              placeholder="Please select"
              options={["Store Associate", "Chat Associate", "Phone Associate", "Other type of associate"]}
            />
            <FeedbackDropdown
              label="Please select your feedback topic?"
              name="feedbackTopic"
              control={control}
              placeholder="Please select"
              options={["Compliment", "Suggestion/Inquiry", "Complaint", "Other"]}
            />
          </>
        )}
        {selectedOption === "Website Feedback" && (
          <>
            <FeedbackDropdown
              label="Please select your feedback topic?"
              name="feedbackTopic"
              control={control}
              placeholder="Please select"
              options={[
                "Technical Issue", "Order Status", "Product", "Search", "Shipping/Delivery", "Navigation", "Suggestion/Compliment"
              ]}
            />
            <FeedbackDropdown
              label="What is your primary purpose for today's visit?"
              name="purposeWebsite"
              control={control}
              placeholder="Please select"
              options={[
                "Make a purchase", "Research/browse products & prices", "Comparison shop", "Check availability of a product", "Schedule/research an installation", "Check order status", "Look for 'How To' information", "Other"
              ]}
            />
            <FeedbackDropdown
              label="Were you able to complete your primary purpose for today's visit?"
              name="primaryPurpose"
              control={control}
              placeholder="Please select"
              options={["Yes", "No", "I'm not finished yet"]}
            />
          </>
        )}
        {selectedOption === "Store Feedback" && (
          <>
            <FeedbackDropdown
              label="What is your main reason for visiting Airport Home Appliance?"
              name="purposeStore"
              control={control}
              placeholder="Please select"
              options={[
                "Make a purchase", "Return or exchange previous purchase", "Warehouse Pickup", "Research or compare products", "Get inspiration for a future project", "Help with a project", "Other"
              ]}
            />
            <FeedbackDropdown
              label="Were you able to accomplish your goal?"
              name="completion"
              control={control}
              placeholder="Please select goal"
              options={["Yes", "No", "I'm not finished yet"]}
            />
            <FeedbackDropdown
              label="For which store location are you providing feedback?"
              name="feedbackStoreLocation"
              control={control}
              placeholder="Please select"
              options={[
                "Hayward", "Hayward Outlet Center", "San Rafael", "San Jose", "Roseville", "Redwood City", "Emeryville", "Dublin", "Concord", "Warehouse Pickup"
              ]}
            />
          </>
        )}

        <TextareaSection
          label="Please share your feedback."
          name="feedbackMessage"
          control={control}
        />

        {selectedOption === "Associate Feedback" && (
          <div className="mb-[26px]">
            <h4 className="text-[13px] leading-[19px] text-[#252525] mb-[12px] font-semibold">
              Where did you interact/observe this associate?
            </h4>
            <Controller
              name="associateLocation"
              control={control}
              rules={{ required: "Location is required" }}
              render={({ field }) => (
                <CustomInput
                  label="Input Here"
                  maxLength={1000}
                  errorMessage={errors?.associateLocation?.message}
                  {...field}
                />
              )}
            />
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4 mt-6">
          <Button
            type="button"
            variant="outline"
            onClick={onBack}
            className="w-[126px] rounded-[4px] h-[46px] border-gray-400 text-gray-600"
          >
            Back
          </Button>
          <Button
            type="submit"
            disabled={!selectedRating}
            className="w-[126px] rounded-[4px] h-[46px] bg-[#0B223C] disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Send Feedback
          </Button>
        </div>
      </form>
    </div>
  );
};

const FeedbackStep2 = ({ onOk }) => (
  <div className="flex flex-col justify-start items-center gap-[22px]">
    <Image src={Feedback} alt="Feedback" width={80} height={80} />
    <div className="text-center">
      <h4 className="font-semibold text-[15px] leading-[22px] text-[#252525] mb-[10px]">Thank you for your feedback!</h4>
      <p className="max-w-[400px] text-[12px] leading-[17px] text-[#252525]">
        We appreciate your feedback. Your opinions will help us provide you the best shopping experience - in store and online.
      </p>
    </div>
    <Button onClick={onOk} className="w-[126px] rounded-[4px] h-[46px] bg-[#0B223C]">Ok</Button>
  </div>
);

const FeedbackDropdown = ({ label, name, placeholder, options, control }) => (
  <div className="mb-[26px]">
    <h4 className="text-[13px] leading-[19px] text-[#252525] mb-[12px] font-semibold">{label}</h4>
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Select onValueChange={field.onChange} value={field.value}>
          <SelectTrigger className="w-[300px] rounded-[10px] h-11 mb-[27px]">
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <SelectContent>
            <SelectGroup>
              {options.map(option => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectGroup>
          </SelectContent>
        </Select>
      )}
    />
  </div>
);

const TextareaSection = ({ label, name, control }) => (
  <div className="mb-[26px]">
    <h4 className="text-[13px] leading-[19px] text-[#252525] mb-[12px] font-semibold">{label}</h4>
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <Textarea
          placeholder="Message Here"
          maxLength={1000}
          className="mt-[10px]"
          {...field}
        />
      )}
    />
  </div>
);

const FeedBack = () => {
  const [isShowFeedback, setIsShowFeedback] = useState(0);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedRating, setSelectedRating] = useState(null);
  const { toast } = useToast();
  const formRef = useRef(null);

  const { control, handleSubmit, reset, formState: { errors } } = useForm({ defaultValues: {} });
  const [contactUs, { data, isSuccess, isLoading, isError, error }] = useContactUsMutation();

  useEffect(() => {
    if (isSuccess && data?.success) {
      toast({ title: "The email has been sent successfully." });
      setIsShowFeedback(2);
      reset();
      setSelectedOption(null);
      setSelectedRating(null);
    }
    if (isError) {
      toast({ title: error?.data?.msg || "Something went wrong." });
    }
  }, [isSuccess, isError, data, error, toast, reset]);

  const onSubmit = async (formData) => {
    if (!selectedOption || !selectedRating) {
      toast({ title: "Please complete all required fields." });
      return;
    }

    const feedbackLabels = {
      "Store Feedback": "the store",
      "Website Feedback": "the website",
      "Associate Feedback": "the associate",
    };
    
    let ratingText = '';

    if (selectedOption === 'Website Feedback' || selectedOption === 'Store Feedback' || selectedOption === 'Associate Feedback') {
      ratingText = `Rate your overall experience with ${feedbackLabels[selectedOption]}:`;
    }
    const payload = {
      subject: selectedOption,
      formType: "Feedback",
      description: `You have received new ${selectedOption}`,
      feedbackType: selectedOption,
      ratingText:ratingText,
      rating: selectedRating,
      ...formData,
    };

    try {
      await contactUs(payload);
    } catch (error) {
      console.error("❌ Error sending email:", error);
      toast({ title: "Failed to send email. Please try again later." });
    }
  };

  return (
    <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <DialogTrigger asChild>
        <Button 
          className="smd:mt-3 py-[11px] px-[19px] border border-blue2 hover:bg-inherit text-blue2 bg-transparent h-[54px] text-[15px] inline-flex items-center gap-3 rounded-[6px]"
          onClick={() => setIsDialogOpen(true)}
        >
          <Image src={FeedbackIcon} alt="Feedback Icon" />
          Feedback
        </Button>
      </DialogTrigger>

      <DialogContent className="max-h-[calc(100%-44px)] overflow-auto">
        <div>
          <DialogHeader>
            <DialogTitle>Feedback</DialogTitle>
            <Image src={Logo} alt="Logo" className="max-w-[116px]" />
          </DialogHeader>

          {isShowFeedback === 0 && (
            <FeedbackStep0 
              selectedOption={selectedOption} 
              setSelectedOption={setSelectedOption} 
              onContinue={() => setIsShowFeedback(1)} 
            />
          )}
          {isShowFeedback === 1 && (
            <FeedbackStep1 
              formRef={formRef} 
              onSubmit={onSubmit} 
              selectedOption={selectedOption} 
              handleSubmit={handleSubmit} 
              control={control} 
              onBack={() => setIsShowFeedback(0)} 
              selectedRating={selectedRating}
              setSelectedRating={setSelectedRating}
              errors={errors}
            />
          )}
          {isShowFeedback === 2 && (
            <FeedbackStep2 onOk={() => {
              setIsShowFeedback(0);
              setIsDialogOpen(false);
            }} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeedBack;